import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Avatar,
  Box,
  Switch,
  FormControlLabel,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowUpward,
  ArrowDownward,
  MoreVert as MoreVertIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Church as ChurchIcon,
  Edit as EditIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import axios from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';

const StaffManagement = () => {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [addDialog, setAddDialog] = useState(false);
  const [newStaff, setNewStaff] = useState({ email: '', name: '' });
  const [permissionsDialog, setPermissionsDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/staff');
      setStaff(response.data);
    } catch (error) {
      setError('Error fetching staff list');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStaff = async () => {
    try {
      await axios.post('/staff', newStaff);
      setSuccess('Staff member added successfully');
      setAddDialog(false);
      setNewStaff({ email: '', name: '' });
      fetchStaff();
    } catch (error) {
      setError('Error adding staff member');
    }
  };

  const handleRemoveStaff = async (email) => {
    try {
      await axios.delete(`/staff/${encodeURIComponent(email)}`);
      setSuccess('Staff member removed successfully');
      fetchStaff();
    } catch (error) {
      setError('Error removing staff member');
    }
  };

  const handlePromote = async (email) => {
    try {
      await axios.post(`/staff/${encodeURIComponent(email)}/promote`);
      setSuccess('Staff member promoted to admin');
      fetchStaff();
    } catch (error) {
      setError('Error promoting staff member');
    }
  };

  const handleDemote = async (email) => {
    try {
      await axios.post(`/staff/${encodeURIComponent(email)}/demote`);
      setSuccess('Admin demoted to staff');
      fetchStaff();
    } catch (error) {
      setError('Error demoting admin');
    }
  };

  const handleUpdatePermissions = async (email, permissions) => {
    try {
      await axios.put(`/staff/${encodeURIComponent(email)}/permissions`, { permissions });
      setSuccess('Permissions updated successfully');
      fetchStaff();
    } catch (error) {
      setError('Error updating permissions');
    }
  };

  const handleOpenPermissions = (member) => {
    setSelectedStaff({
      ...member,
      permissions: member.permissions || {
        ccdRegistrations: false,
        parishRegistrations: false,
        staffManagement: false,
        massManagement: false,
        ociaRegistrations: false,
        campusMinistryRegistrations: false
      }
    });
    setPermissionsDialog(true);
    handleCloseMenu();
  };

  const handlePermissionChange = (permission) => {
    setSelectedStaff(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: !prev.permissions[permission]
      }
    }));
  };

  const handleSavePermissions = () => {
    handleUpdatePermissions(selectedStaff.email, selectedStaff.permissions);
    setPermissionsDialog(false);
    setSelectedStaff(null);
  };

  const handleOpenMenu = (event, member) => {
    setMenuAnchor(event.currentTarget);
    setSelectedMember(member);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSelectedMember(null);
  };

  const getPermissionCount = (member) => {
    const permissions = member.permissions || {};
    return Object.values(permissions).filter(Boolean).length;
  };

  const getPermissionChips = (member) => {
    const permissions = member.permissions || {};
    const chips = [];

    if (permissions.ccdRegistrations) {
      chips.push({ label: 'CCD', icon: <SchoolIcon />, color: 'primary' });
    }
    if (permissions.parishRegistrations) {
      chips.push({ label: 'Parish', icon: <PeopleIcon />, color: 'secondary' });
    }
    if (permissions.staffManagement) {
      chips.push({ label: 'Staff', icon: <SecurityIcon />, color: 'warning' });
    }
    if (permissions.massManagement) {
      chips.push({ label: 'Mass', icon: <ChurchIcon />, color: 'info' });
    }
    if (permissions.ociaRegistrations) {
      chips.push({ label: 'OCIA', icon: <PersonIcon />, color: 'success' });
    }
    if (permissions.campusMinistryRegistrations) {
      chips.push({ label: 'Campus Ministry', icon: <GroupsIcon />, color: 'primary' });
    }

    return chips;
  };

  const isCurrentUser = (email) => user && user.email === email;
  const isAdmin = user && user.isAdmin;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ fontSize: 40, mr: 2 }} />
            <Box>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 600 }}>
                Staff Management
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Manage staff accounts and permissions
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialog(true)}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
              }
            }}
          >
            Add Staff
          </Button>
        </Box>
      </Paper>

      {/* Staff Cards */}
      {loading ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>Loading staff...</Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {staff
            .slice()
            .sort((a, b) => {
              // Sort by admin status first (admins first), then alphabetically by name
              if (a.isAdmin && !b.isAdmin) return -1;
              if (!a.isAdmin && b.isAdmin) return 1;

              const nameA = (a.name || a.email).toLowerCase();
              const nameB = (b.name || b.email).toLowerCase();
              return nameA.localeCompare(nameB);
            })
            .map((member) => {
            const permissionChips = getPermissionChips(member);
            const permissionCount = getPermissionCount(member);

            return (
              <Grid item xs={12} md={6} lg={4} key={member.email}>
                <Card
                  elevation={3}
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardContent>
                    {/* Header with Avatar and Admin Badge */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        src={member.profilePictureUrl}
                        sx={{
                          width: 56,
                          height: 56,
                          mr: 2,
                          backgroundColor: member.isAdmin ? '#1976d2' : '#757575'
                        }}
                      >
                        {member.isAdmin ? <AdminIcon /> : <PersonIcon />}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                          {member.name || 'No Name'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {member.email}
                        </Typography>
                        {member.isAdmin && (
                          <Chip
                            icon={<AdminIcon />}
                            label="Administrator"
                            color="primary"
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                        {isCurrentUser(member.email) && (
                          <Chip
                            label="You"
                            color="success"
                            size="small"
                            sx={{ mt: 0.5, ml: member.isAdmin ? 1 : 0 }}
                          />
                        )}
                      </Box>
                      {isAdmin && !isCurrentUser(member.email) && (
                        <IconButton
                          onClick={(e) => handleOpenMenu(e, member)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      )}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Permissions Section */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <SettingsIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="subtitle2" color="text.secondary">
                          Permissions
                        </Typography>
                      </Box>

                      {member.isAdmin ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Chip
                            icon={<AdminIcon />}
                            label="Full Access"
                            color="primary"
                            size="small"
                            variant="filled"
                          />
                        </Box>
                      ) : permissionChips.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {permissionChips.map((chip, index) => (
                            <Chip
                              key={index}
                              icon={chip.icon}
                              label={chip.label}
                              color={chip.color}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No permissions assigned
                        </Typography>
                      )}
                    </Box>
                  </CardContent>

                  {isAdmin && !isCurrentUser(member.email) && (
                    <CardActions sx={{ px: 2, pb: 2 }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenPermissions(member)}
                        variant="outlined"
                        fullWidth
                      >
                        Edit Permissions
                      </Button>
                    </CardActions>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => handleOpenPermissions(selectedMember)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Permissions</ListItemText>
        </MenuItem>
        {selectedMember && (
          <MenuItem onClick={() => selectedMember.isAdmin ? handleDemote(selectedMember.email) : handlePromote(selectedMember.email)}>
            <ListItemIcon>
              {selectedMember.isAdmin ? <ArrowDownward fontSize="small" /> : <ArrowUpward fontSize="small" />}
            </ListItemIcon>
            <ListItemText>
              {selectedMember.isAdmin ? 'Remove Admin' : 'Make Admin'}
            </ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { handleRemoveStaff(selectedMember?.email); handleCloseMenu(); }} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Remove Staff</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add Staff Dialog */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AddIcon sx={{ mr: 1 }} />
            Add Staff Member
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Full Name"
            fullWidth
            value={newStaff.name}
            onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={newStaff.email}
            onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
            helperText="Staff member will receive access upon first Google login"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setAddDialog(false)}>Cancel</Button>
          <Button onClick={handleAddStaff} variant="contained" startIcon={<AddIcon />}>
            Add Staff Member
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={permissionsDialog} onClose={() => setPermissionsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 1 }} />
            Edit Permissions - {selectedStaff?.name || selectedStaff?.email}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select which areas this staff member can access and manage.
          </Typography>

          <FormGroup>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedStaff?.permissions?.ccdRegistrations || false}
                    onChange={() => handlePermissionChange('ccdRegistrations')}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="subtitle1">CCD Registrations</Typography>
                      <Typography variant="body2" color="text.secondary">
                        View and manage Religious Education registrations
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedStaff?.permissions?.parishRegistrations || false}
                    onChange={() => handlePermissionChange('parishRegistrations')}
                    color="secondary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleIcon sx={{ mr: 1, color: 'secondary.main' }} />
                    <Box>
                      <Typography variant="subtitle1">Parish Registrations</Typography>
                      <Typography variant="body2" color="text.secondary">
                        View and manage parish family registrations
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedStaff?.permissions?.staffManagement || false}
                    onChange={() => handlePermissionChange('staffManagement')}
                    color="warning"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SecurityIcon sx={{ mr: 1, color: 'warning.main' }} />
                    <Box>
                      <Typography variant="subtitle1">Staff Management</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add, remove, and manage staff permissions
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedStaff?.permissions?.massManagement || false}
                    onChange={() => handlePermissionChange('massManagement')}
                    color="info"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ChurchIcon sx={{ mr: 1, color: 'info.main' }} />
                    <Box>
                      <Typography variant="subtitle1">Mass Management</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Manage Mass schedules, intentions, and clergy assignments
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedStaff?.permissions?.ociaRegistrations || false}
                    onChange={() => handlePermissionChange('ociaRegistrations')}
                    color="success"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 1, color: 'success.main' }} />
                    <Box>
                      <Typography variant="subtitle1">OCIA Registrations</Typography>
                      <Typography variant="body2" color="text.secondary">
                        View and manage OCIA (adult initiation) registrations
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedStaff?.permissions?.campusMinistryRegistrations || false}
                    onChange={() => handlePermissionChange('campusMinistryRegistrations')}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <GroupsIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="subtitle1">Campus Ministry</Typography>
                      <Typography variant="body2" color="text.secondary">
                        View and manage Fall Retreat and other Campus Ministry signups (includes medical information)
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </Paper>
          </FormGroup>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Administrators have full access to all areas regardless of individual permissions.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setPermissionsDialog(false)}>Cancel</Button>
          <Button onClick={handleSavePermissions} variant="contained" startIcon={<SettingsIcon />}>
            Save Permissions
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StaffManagement; 